import React, { useCallback, useRef, useState } from 'react';
import Table from '../../components/Table';
import { useTranslation } from 'react-i18next';
import api from 'api';
import useLabels from './hooks/useLabels';
import Modal, { ModalRef } from 'components/Modal';
import LocationTypeForm from './components/LocationTypeForm';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import utils from 'utils';

export type LocationType = {
  id: number;
  type: string;
};

const InventoryLocationTypes = () => {
  const { t } = useTranslation();

  const [currentType, setCurrentType] = useState<LocationType>();

  const labels = useLabels();

  const modalRef = useRef<ModalRef>();
  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery(
    ['locationTypes'],
    async () => {
      const { data } = await api.inventoryLocations.getLocationTypes();
      return data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const addInventoryType = useCallback(
    async (name: string) => {
      try {
        await api.inventoryLocations.addLocationType(name);
        toast.success(t('InventoryLocationTypes.successAddingType'));
        modalRef.current.close();
        setCurrentType(null);
        queryClient.invalidateQueries({
          queryKey: ['locationTypes'],
        });
      } catch (error) {
        toast.error(utils.handleResponseError(error));
      }
    },
    [queryClient, t],
  );

  const updateInventoryType = useCallback(
    async (name: string, id: number) => {
      try {
        await api.inventoryLocations.editLocationType(name, id);
        toast.success(t('InventoryLocationTypes.successUpdatingType'));
        modalRef.current.close();
        setCurrentType(null);
        queryClient.invalidateQueries({
          queryKey: ['locationTypes'],
        });
      } catch (error) {
        toast.error(utils.handleResponseError(error));
      }
    },
    [queryClient, t],
  );
  const onSubmit = useCallback(
    async (type: string, id?: number) => {
      if (id) {
        updateInventoryType(type, id);
      } else {
        addInventoryType(type);
      }
    },
    [addInventoryType, updateInventoryType],
  );

  const renderContentArray = useCallback(
    (data: { id: number; type: string }[]) =>
      data?.map(({ id, ...rest }) => ({
        id: `${id}`,
        ...rest,
      })),
    [],
  );

  return (
    <>
      <Table
        title={t('InventoryLocationTypes.title')}
        labels={labels}
        totalItems={data?.length}
        totalPages={1}
        elementProps={labels.map(({ key }) => key)}
        buttonText={t('InventoryLocations.add')}
        onAddButtonClick={() => modalRef.current.open()}
        fetching={isFetching}
        withActionButtons
        onEdit={(el) => {
          setCurrentType({ id: +el.id, type: el.type });
          modalRef.current.open();
        }}
        contentArray={renderContentArray(data)}
      />
      <Modal
        focusableElements="div"
        type="no-action"
        ref={modalRef}
        onClose={() => setCurrentType(null)}
        onX={() => {
          setCurrentType(null);
          modalRef.current.close();
        }}
        modalName="manage-inventory-location-types"
        title={
          currentType
            ? t('InventoryLocationTypes.edit')
            : t('InventoryLocationTypes.add')
        }
      >
        <LocationTypeForm
          initialType={currentType}
          handleCancel={() => modalRef.current.close()}
          onSubmit={(values) => {
            onSubmit(values.type, values?.id);
          }}
        />
      </Modal>
    </>
  );
};

export default InventoryLocationTypes;
